import AddIcon from '@mui/icons-material/Add'
import AnnouncementOutlinedIcon from "@mui/icons-material/AnnouncementOutlined"
import ArrowRightIcon from '@mui/icons-material/ArrowRight'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import EditIcon from '@mui/icons-material/Edit'
import { CircularProgress, IconButton, InputBase, LinearProgress, List, ListItem, Skeleton, Stack, Tooltip, Typography } from '@mui/material'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import { useTheme } from '@mui/material/styles'
import { Box } from '@mui/system'
import { useKeycloak } from '@react-keycloak/web'
import { useQueryClient } from '@tanstack/react-query'
import YouTube from '@u-wave/react-youtube'
import axios from 'axios'
import { atom, useAtom, useAtomValue } from 'jotai'
import * as React from 'react'
import { useState } from 'react'
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'
import validator from 'validator'
import { baseAtom, selectVideo, setLastExpandedWikiSection } from '../../stores/jotai/base'
import { discoverySessionAtom } from '../../stores/jotai/discovery-session'
import { setWikiSectionsExpansionStatus, WikiSectionsExpansionAtom } from '../../stores/jotai/wiki/wikiSectionsExpansion'
import { useMutateWiki } from '../../tanstackQuery/queries/wiki'
import { externalizeUrl } from '../../util/feature_utils'
import { WIKI_SECTION_CHANGE_EVENTS } from './constants'
import { placeholderAtom } from './wiki-toolbar'
import DeleteIcon from '../../../icons/DeleteIcon'
import ValidateIcon from '../../../icons/ValidateIcon'
import InvalidateIcon from "../../../icons/InvalidateIcon"
import DeleteOutlinedIcon from '../../../icons/DeleteOutlinedIcon'


const ENTER_KEY_CODE = 13;

const placeholderValueAtom = atom('')

function ConfirmCancelButtons(props) {
  const [validateHoverd, setValidateHoverd] = React.useState(false);
  const [invalidateHoverd, setInvalidateHoverd] = React.useState(false);

  const { confirmFunc, cancelFunc, addLabel } = props;
  return (
    <div style={{ display: "flex", justifyContent: "flex-end" }}>
      <Tooltip title={addLabel ? addLabel : "Add Link"}>
        <IconButton onClick={confirmFunc}>
          <ValidateIcon
            hovered={validateHoverd}
            onMouseEnter={() => setValidateHoverd(true)}
            onMouseLeave={() => setValidateHoverd(false)}
            sx={{ color: "black" }}
          />
        </IconButton>
      </Tooltip>
      <Tooltip title="Cancel">
        <IconButton onClick={cancelFunc}>
          <InvalidateIcon
            hovered={invalidateHoverd}
            onMouseEnter={() => setInvalidateHoverd(true)}
            onMouseLeave={() => setInvalidateHoverd(false)}
            sx={{ color: "black" }}
          />
        </IconButton>
      </Tooltip>
    </div>
  );
}

const WikiLoader = (props) => {
  const { show, size } = props;

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0, 0, 0, 0.2)",
        zIndex: 1,
        opacity: show ? 1 : 0,
        pointerEvents: show ? "auto" : "none",
        transition: "opacity 0.2s ease",
      }}
    >
      <CircularProgress size={size} />
    </Box>
  );
}

const isValidLink = (url) => {
  return validator.isURL(url);
}

function WikiSection(props) {
  const { idx, editable, section } = props;

  const key = props?.section?.sectionID;
  const wikiID = props?.section?.wikiID;

  const [linkPlaceholder, setLinkPlaceholder] = React.useState(false)
  const [placeholderValues, setPlaceholderValues] = React.useState({ name: '', url: '' })
  const [linkBeingEdited, setLinkBeingEdited] = React.useState(null)
  const [hoveredLink, setHoveredLink] = React.useState(null)
  const [hoveredSection, setHoveredSection] = React.useState(null)
  const [placeholderValidation, setPlaceholderValidation] = useState("");
  const [localSection, setLocalSection] = React.useState(null);
  const [sectionName, setSectionName] = React.useState(null);
  const [isSavingNewLink, setIsSavingNewLink] = useState(false);
  const [isSavingLinkChanges, setIsSavingLinkChanges] = useState(false);
  const [isDeletingSection, setIsDeletingSection] = useState(false);
  const [isDeletingLink, setIsDeletingLink] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [base, setBase] = useAtom(baseAtom);
  const [wikiSectionsExpansion, setWikiSectionsExpansion] = useAtom(
      WikiSectionsExpansionAtom
  );
  const { keycloak } = useKeycloak();
  const theme = useTheme();

  // Section-level Handlers 
  const updateSectionTitle = () => {
    if (props.section.name !== sectionName) {
      const modifiedSection = { ...props.section, name: sectionName };

      props.handleChange({
        action: WIKI_SECTION_CHANGE_EVENTS.UPDATE,
        payload: modifiedSection,
        wikies: props?.wikies,
        onSuccess: () => {},
        onFailure: (err) => {
          setSectionName(props?.section?.name);
        },
      });
    }
  };
  
  const handleDeleteSection = () => {
    setIsDeletingSection(true);

    props.handleChange({
      action: WIKI_SECTION_CHANGE_EVENTS.DELETE,
      payload: props?.section,
      wikies: props?.wikies,
      onSuccess: () => {
        setLocalSection(null);
        setIsDeletingSection(false);
      },
      onFailure: (err) => {
        setLocalSection(props.section);
        setIsDeletingSection(false);
      },
    });
  }

  const handleSectionHover = (e) => {
    if (e.type === 'mouseenter') {
      setHoveredSection(props?.section)
    }
    if (e.type === 'mouseleave') {
      setHoveredSection(null)
    }
  }

  // General Link Handlers
  const handleLinkHover = (e, link) => {
    if (e.type === 'mouseenter') {
      setHoveredLink(link)
    }
    if (e.type === 'mouseleave') {
      setHoveredLink(null)
    }
  }
  const handleRemoveLink = (linkToDelete) => {
    const modifiedSection = {
      ...props?.section,
      linkSet: props?.section?.linkSet?.filter(
        (link) => link?.linkID !== linkToDelete?.linkID
      ),
    };

    setIsDeletingLink(true);

    props.handleChange({
      action: WIKI_SECTION_CHANGE_EVENTS.UPDATE,
      payload: modifiedSection,
      wikies: props?.wikies,
      onSuccess: () => {
        setLocalSection(modifiedSection);
        setIsDeletingLink(false);
      },
      onFailure: (err) => {
        setLocalSection(props?.section);
        setIsDeletingLink(false);
      },
    });
  }

  // Add New Links
  const handleAddLinkPlaceholder = (e) => {
    e.stopPropagation();

    if (!isExpanded) {
      expandSection();
    }

    setLastExpandedWikiSection(setBase, wikiID, key);
    setLinkPlaceholder(true);
  }

  const isPlaceholderValid = () => {
    return isValidLink(placeholderValues?.url);
  }

  const handleUpdatePlaceholderName = (e) => {
    setPlaceholderValues(v => ({
      ...v,
      name: e.target.value
    }))
  }

  const handleUpdatePlaceholderURL = (e) => {
    setPlaceholderValues(v => ({
      ...v,
      url: e.target.value
    }))
  }

  const handleAddNewLink = () => {
    if (isPlaceholderValid()) {
      if (placeholderValues.name !== '' && placeholderValues.url !== '') {
        const newLink = {
          linkID: crypto.randomUUID(),
          sectionID: props?.section?.sectionID,
          sortOrder: props?.section?.linkSet?.length + 1,
          created: new Date().toUTCString(),
          name: placeholderValues?.name,
          url: placeholderValues?.url,
        };
        const modifiedSection = {
          ...props.section,
          linkSet: [...props?.section?.linkSet, newLink],
        };

        setIsSavingNewLink(true);

        props.handleChange({
          action: WIKI_SECTION_CHANGE_EVENTS.UPDATE,
          payload: modifiedSection,
          wikies: props?.wikies,
          onSuccess: () => {
            setLocalSection(modifiedSection);
            setPlaceholderValues({ name: "", url: "" });
            setLinkPlaceholder(false);
            setPlaceholderValidation("");
            setIsSavingNewLink(false);
          },
          onFailure: (err) => {
            setLocalSection(props?.section);
            setIsSavingNewLink(false);
          },
        });
      }
    }
    else {
      setPlaceholderValidation("The entered value must be a valid URL.")
    }

  }
  const handleNewLinkCancel = () => {
    setPlaceholderValues({ name: '', url: '' })
    setLinkPlaceholder(false)
  }

  // Edit Links 
  const handleEditLink = (link) => {
    setLinkBeingEdited(link)
  }

  const handleUpdateLinkName = (e) => {
    setLinkBeingEdited(prev => ({
      ...prev,
      name: e.target.value
    }))
  }

  const handleUpdateLinkURL = (e) => {
    setLinkBeingEdited(prev => ({
      ...prev,
      url: e.target.value
    }))
  }

  const handleConfirmEdit = () => {
    const modifiedSection = {
      ...props.section,
      linkSet: props?.section?.linkSet?.map(link => link?.linkID === linkBeingEdited?.linkID ? linkBeingEdited : link),
    };

    setIsSavingLinkChanges(true);

    props.handleChange({
      action: WIKI_SECTION_CHANGE_EVENTS.UPDATE,
      payload: modifiedSection,
      wikies: props?.wikies,
      onSuccess: () => {
        setLocalSection(modifiedSection);
        setLinkBeingEdited(null);
        setIsSavingLinkChanges(false);
      },
      onFailure: (err) => {
        setLocalSection(props?.section);
        setIsSavingLinkChanges(false);
      },
    });
  }
  const handleCancelEdit = () => {
    setLinkBeingEdited(null)
  }

  const expandSection = () => {
      setWikiSectionsExpansionStatus(
          setWikiSectionsExpansion,
          section?.sectionID,
          true
      );
  };

  const onExpansionChange = (event, expanded) => {
    setWikiSectionsExpansionStatus(
        setWikiSectionsExpansion,
        section?.sectionID,
        expanded
    );
  }

  const linkFormStyles = {
    position: "relative",
    padding: "5px",
    borderRadius: '4px',
    backgroundColor: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexGrow: 1,
    margin: "10px 0",
    boxShadow: '0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%), 0 1px 4px rgb(0 0 0 / 23%)'
  }
  const linkInputStyles = {
    margin: '0px 5px',
    paddingX: '5px',
    backgroundColor: 'rgba(23, 119, 209, .05)'
  }

  const newLinkForm = <div
    style={{
      margin: '10px 0',
      ...linkFormStyles,
    }}>
    <WikiLoader show={isSavingNewLink} size={40} />
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', width: '75%' }}>
      <InputBase sx={linkInputStyles} onKeyDown={(e) => {
        if (e.key === 'Enter') {
          handleAddNewLink();
        }
        e.stopPropagation();
      }} autoFocus={true} placeholder="Link Name" value={placeholderValues?.name} onChange={handleUpdatePlaceholderName} />
      <InputBase sx={linkInputStyles} placeholder="Link URL"
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            handleAddNewLink();
          }
          e.stopPropagation();
        }}
        value={placeholderValues.url} onChange={handleUpdatePlaceholderURL} />
    </div>
    <ConfirmCancelButtons confirmFunc={handleAddNewLink} cancelFunc={handleNewLinkCancel} />
  </div>

  const updateLinkForm = <div
    style={{
      ...linkFormStyles
    }}>
    <WikiLoader show={isSavingLinkChanges} size={40} />
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', width: '75%' }}>
      <div style={{ display: 'flex', alignItems: 'center', paddingLeft: '10px' }}>
        Name:
        <InputBase sx={{ ...linkInputStyles, width: '100%' }} placeholder="New Name" value={linkBeingEdited?.name} onChange={handleUpdateLinkName} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', paddingLeft: '20px' }}>
        URL:
        <InputBase sx={{ ...linkInputStyles, width: '100%' }} placeholder="New URL" value={linkBeingEdited?.url} onChange={handleUpdateLinkURL} />
      </div>
    </div>
    <ConfirmCancelButtons confirmFunc={handleConfirmEdit} cancelFunc={handleCancelEdit} />
  </div>;

  const accordionActions = editable ? <div style={{ display: 'flex', justifyContent: 'flex-end', paddingRight: '8px' }}>
    <Tooltip title="Delete Section">
      <IconButton onClick={handleDeleteSection} sx={{ padding: '7px 5px 7px 8px' }}>
       {/* <IconButton sx={{  padding:'4px 2px 4px 5px' }} onClick={() => this.remove()}> */}
       <DeleteOutlinedIcon sx={{
              color: "gray",
              fill:"none",
              "&:hover": {
                color: "#1976D2",
              },
            }}/>
            {/* </IconButton> */}
      </IconButton>
    </Tooltip>
    <Tooltip title="Add Link to Section">
      <IconButton onClick={handleAddLinkPlaceholder} sx={{ padding: '3px' }}>
        <AddIcon />
      </IconButton>
    </Tooltip>
  </div> : null

  let accordionTitle = editable ? (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        width: "100%",
      }}
    >
      <InputBase
        sx={{
          width: "auto",
          flexGrow: "1",
          marginRight: "8px",
          "& .MuiInputBase-input": { textOverflow: "ellipsis" },
          fontWeight: 600,
        }}
        value={sectionName}
        onChange={(e) => setSectionName(e.target.value)}
        onKeyDown={(e) => {
          if (e.keyCode === ENTER_KEY_CODE) {
            updateSectionTitle();
          }
        }}
        onBlur={() => {
          updateSectionTitle();
        }}
        onClick={(e) => e.stopPropagation()}
      />
      {accordionActions}
    </div>
  ) : (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        width: "100%",
        fontWeight: 600,
      }}
    >
      {localSection?.name}
    </div>
  );

  const markVideoLinkAsSeen = (linkID) => {
    const { cohort } = base;

    axios
        .put(
            `${process.env.NEXT_PUBLIC_LINX_URL}/seen`,
            {
                type: "link",
                cohortID: cohort?.id,
                targets: [linkID],
            },
            {
                withCredentials: false,
                headers: {
                    Authorization: "Bearer " + keycloak?.token,
                },
            }
        )
        .catch((error) => {
            console.error("Couldn't mark link as seen.");
        });
  }


  const accordionContent = <List sx={{ paddingTop: '0px', paddingBottom: '10px', maxHeight: '240px' }}>
    {localSection?.linkSet?.map((l, idx) => {

      let isSelected = false;

      try {
        const potentialAction = l.url.replace("https://youtube.com/", "").replace("https://youtu.be/", "");
        if (potentialAction === base.selectedVideo) {
          isSelected = true;
        }
      } catch(e) {

      }

      let urlStyle = {
        padding: "4px 10px 4px 10px",
        marginLeft: "-8px",
        marginRight: "6px",
        borderRadius: "0px 4px 4px 0px",
        width: "auto",
        flexGrow: 1,
        color: theme?.custom?.mainButtonHighlight,
        transition: "background-color 0.2s ease",
      };

      if (isSelected) {
        urlStyle = {
          ...urlStyle,
          backgroundColor: theme?.custom?.mainButtonHighlight,
          color: "white",
          ":hover": {
            backgroundColor: theme?.custom?.mainButtonHighlight,
          },
        };
      }

      const getHoveredLinkBackgroundColor = () => {
        if (isSelected) {
          return theme?.custom?.mainButtonHighlight;
        }

        if (linkBeingEdited?.linkID !== l.linkID) {
          return theme?.custom?.hoverOptionColor;
        }

        return "transparent";
      }
    

      return (
        <Box
          onMouseEnter={(e) => handleLinkHover(e, l)}
          onMouseLeave={(e) => handleLinkHover(e, l)}
          key={l.linkID}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            transition: "background-color 0.2s ease",
            ":hover": {
              ".MuiListItem-root": {
                cursor: "pointer",
                backgroundColor: getHoveredLinkBackgroundColor(),
              },
            },
            ":not(:last-child)": {
              marginBottom: "4px",
            },
          }}
        >
          {linkBeingEdited && linkBeingEdited?.linkID === l?.linkID ? (
            <ListItem sx={{ padding: "0px", width: "auto", flexGrow: 1 }}>
              {updateLinkForm}
            </ListItem>
          ) : (
            <ListItem
              key={l.linkID}
              onClick={() => {
                props.setHasSelectedAnyVideosThisSession(true);

                const url = l.url;

                if (url.includes("youtube.com") || url.includes("youtu.be")) {
                  const target = url
                    .replace("https://youtube.com/", "")
                    .replace("https://youtu.be/", "");
                  const { sectionID } = props.section;

                  selectVideo(setBase, target, sectionID);
                  markVideoLinkAsSeen(l.linkID);
                } else {
                  open(externalizeUrl(url));
                }
              }}
              sx={urlStyle}
            >
              <Typography sx={{ fontWeight: "unset" }}>{l?.name}</Typography>
            </ListItem>
          )}
          {editable && linkBeingEdited?.linkID !== l?.linkID ? (
            <div
              style={{
                display: "flex",
                opacity:
                  editable &&
                  hoveredLink &&
                  hoveredLink?.linkID === l?.linkID
                    ? "1"
                    : 0,
                transition: "opacity 0.2s ease",
              }}
            >
              <Tooltip title="Edit Link">
                <IconButton
                  onClick={() => handleEditLink(l)}
                  sx={{ padding: "3px" }}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete Link">
                <IconButton
                  onClick={() => handleRemoveLink(l)}
                  sx={{ padding: '7px 5px 7px 8px' }}
                >
                {/* <IconButton sx={{  padding:'4px 2px 4px 5px' }} onClick={() => this.remove()}> */}
       <DeleteOutlinedIcon sx={{
              color: "gray",
              fill:"none",
              "&:hover": {
                color: "#1976D2",
              },
            }}/>
            {/* </IconButton> */}
                </IconButton>
              </Tooltip>
            </div>
          ) : null}
        </Box>
      );
    })}
    {linkPlaceholder ? newLinkForm : null}
    <div style={{ display: 'block', width: '100%', height: '10px' }}></div>
  </List >

  const CustomDragHandle = (dragProps) => {
    return <div style={{ display: 'flex', alignItems: 'center', width: '22.5px' }} {...dragProps}>{hoveredSection === props.section ? <DragIndicatorIcon sx={{}} /> : ''}</div>
  }

  const fullAccordion = (
    <Box
      sx={{
        position: "relative",
        flexGrow: 1,
      }}
    >
      <WikiLoader
        show={isDeletingSection || isDeletingLink}
        size={36}
      />
      <Accordion
        key={key}
        defaultExpanded={
          base?.slectedVideoWikiSectionID
            ? base?.slectedVideoWikiSectionID === key
            : idx === 0
        }
        sx={{
          width: "100%",
          "&.Mui-expanded": { margin: "0px" },
          ":before": { backgroundColor: "transparent" },
          ":last-of-type": { borderRadius: "4px" },
        }}
        expanded={isExpanded}
        onChange={onExpansionChange}
      >
        <AccordionSummary
          sx={{
            "&, &.Mui-expanded": {
              minHeight: "40px",
              height: "40px",
              backgroundColor: "rgb(250, 250, 250)",
              borderRadius: "4px",
            },
          }}
          expandIcon={
            <Tooltip title="Show/Hide Section">
              <IconButton sx={{ padding: "0px" }}>
                <ArrowRightIcon sx={{ fontSize: "30px" }} />
              </IconButton>
            </Tooltip>
          }
        >
          {accordionTitle}
        </AccordionSummary>
        <AccordionDetails
          sx={{
            maxHeight: "240px",
            overflowY: "auto",
            padding: "8px 2px 8px 8px",
          }}
        >
          {accordionContent}
        </AccordionDetails>
      </Accordion>
    </Box>
  );

  React.useEffect(() => {
    setLocalSection(props?.section);
  }, [props?.section]);

  React.useEffect(() => {
    if (!sectionName && localSection) {
      setSectionName(localSection?.name);
    }
  }, [localSection]);

  React.useEffect(() => {
    if (sectionName) {
      updateSectionTitle();
    }
  }, [sectionName]);

  React.useEffect(() => {
      setIsExpanded(!!wikiSectionsExpansion[section?.sectionID]);
  }, [wikiSectionsExpansion[section?.sectionID]]);

  if (!localSection) {
    return null;
  }

  return (editable ? <Draggable key={props?.section?.sectionID} draggableId={props?.section?.sectionID} index={idx}>
    {(provided, snapshot) => {
      return <div
        {...provided.draggableProps}
        className={props?.section?.expanded ? 'Draggable Expanded' : 'Draggable'}
        ref={provided.innerRef}
        onMouseEnter={handleSectionHover}
        onMouseLeave={handleSectionHover}
        key={props.section.sectionID + "_"}
        style={{
          // height: `${51 + (wikiSection.links.length * 60)}px`, 
          display: 'flex',
          userSelect: 'none',
          backgroundColor: 'transparent',
          margin: '10px 0px',
          ...provided.draggableProps.style
        }}
      >
        <CustomDragHandle {...provided.dragHandleProps} />
        {fullAccordion}
      </div>
    }}
  </Draggable> : <div style={{ paddingLeft: '22.5px', margin: '10px 0px' }}>
    {fullAccordion}
  </div>)
}

export default function MaterialWiki(props) {
  const [sections, setSections] = React.useState([])
  const [sessionVideo, setSessionVideo] = React.useState("");
  const [hasSelectedAnyVideosThisSession, setHasSelectedAnyVideosThisSession] = React.useState(false);
  const [base, setBase] = useAtom(baseAtom);
  const [needPlaceholder, setNeedPlaceholder] = useAtom(placeholderAtom)
  const [placeholderValue, setPlaceholderValue] = useAtom(placeholderValueAtom)
  const [readyForRender, setReadyForRender] = useState(false);
  const [isSavingNewSection, setIsSavingNewSection] = useState(false);
  const [isSavingSectionOrders, setIsSavingSectionOrders] = useState(false);
  const { keycloak } = useKeycloak();
  const discoverySession = useAtomValue(discoverySessionAtom);
  const theme = useTheme();
  let wikiID = undefined;

  if (props?.wiki) {
    wikiID = props?.wiki?.wikiID;
  }

  const client = useQueryClient();
  const { mutateAsync: mutateWiki } = useMutateWiki(
    { accessToken: keycloak?.token },
    {
      onSuccess: () => {
        client.invalidateQueries({ queryKey: ["wikies"] });
      }
    }
  );

  const handlePlaceholderUpdate = (e) => {
    setPlaceholderValue(e.target.value)
  }
  const handlePlaceholderCancel = (e) => {
    setPlaceholderValue('')
    setNeedPlaceholder(false)
  }

  React.useEffect(() => {
    setSections(props?.sectionsAtom || []);
  }, [props.sectionsAtom]);

  React.useEffect(() => {
    // when we mount the video video, ensure something is selection
    if (sections?.length > 0 && props.videoLibrary === true) {
      if (!base.selectedVideo) {
        const url = sections[0]?.linkSet[0]?.url?.replace("https://youtube.com/", "").replace("https://youtu.be/", "");
        const { sectionID } = sections[0];

        setLastExpandedWikiSection(setBase, wikiID, sections[0]?.sectionID);
        selectVideo(setBase, url, sectionID);
        setHasSelectedAnyVideosThisSession(true);
        setReadyForRender(true);
      }
      else {
        // we already have a video selected
        setHasSelectedAnyVideosThisSession(true);
        setReadyForRender(true);
      }
    }
    else {
      // no initial video selection
      setReadyForRender(true);
    }
  }, [wikiID, sections]);

  React.useEffect(() => {
    if (hasSelectedAnyVideosThisSession) {
      setSessionVideo(base?.selectedVideo);
    }
  }, [base?.selectedVideo, hasSelectedAnyVideosThisSession]);

  const handleAddSection = () => {
    if (placeholderValue?.length !== 0) {
      if (props?.wiki) {
        setIsSavingNewSection(true);

        handleWikiChange({
          action: WIKI_SECTION_CHANGE_EVENTS.CREATE,
          payload: {
            name: placeholderValue,
            wikiID: props?.wiki?.wikiID,
          },
          wikies: props?.wikies,
          onSuccess: (newSection) => {
            setPlaceholderValue("");
            setNeedPlaceholder(false);
            setIsSavingNewSection(false);
            setSections((prevSections) => [...prevSections, newSection]);
          },
          onFailure: () => {
            setIsSavingNewSection(false);},
        });
      }
    }
  }

  const handleEnterSubmit = (e) => {
    if (e.key === "Enter") {
      handleAddSection()
    }
  }

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    try {
      setIsSavingSectionOrders(true);

      const { source, destination } = result;
      //@ts-ignore
      const copiedSections = [...sections];
      const [removed] = copiedSections?.splice(source?.index, 1);
      
      copiedSections?.splice(destination.index, 0, removed);

      const reorderedSections = copiedSections?.map((section, idx) => ({ ...section, sortOrder: idx + 1 }));
      
      setSections(reorderedSections);

      handleWikiChange({
        action: WIKI_SECTION_CHANGE_EVENTS.REORDER,
        payload: {
          wikiID: props?.wiki?.wikiID,
          reorderedSections,
        },
        wikies: props?.wikies,
        onSuccess: (newSection) => {
          setIsSavingSectionOrders(false);
        },
        onFailure: () => {
          setIsSavingSectionOrders(false);
          setSections(sections);
        },
      });
    } catch (e) {
      setIsSavingSectionOrders(false);

      console.error(e);
    }
  };

  const handleWikiChange = ({
    action,
    wikies,
    payload,
    onSuccess,
    onFailure
  }) => {
    const modifiedWiki = wikies?.wikis?.find(
      (wiki) => wiki?.wikiID === payload?.wikiID
    );
    if (action === WIKI_SECTION_CHANGE_EVENTS.DELETE) {
      const wikiSections = modifiedWiki.sections.filter(
        (section) => section?.sectionID !== payload?.sectionID
      );
      mutateWiki({
        sessionID: discoverySession?.sessionID,
        wikiID: modifiedWiki?.wikiID,
        wikiSections
      })
        .then((res) => {
          onSuccess(res);
          onFailure();
        })
        .catch();
    } else if (action === WIKI_SECTION_CHANGE_EVENTS.UPDATE) {
      const wikiSections = modifiedWiki?.sections?.map((section) =>
        section?.sectionID === payload?.sectionID ? payload : section
      );
      mutateWiki({
        sessionID: discoverySession?.sessionID,
        wikiID: modifiedWiki?.wikiID,
        wikiSections
      })
        .then((res) => {
          onSuccess(res);
          onFailure();
        })
        .catch();
    } else if (action === WIKI_SECTION_CHANGE_EVENTS.CREATE) {
      const newSectionScaffolding = {
        sectionID: crypto.randomUUID(),
        wikiID: modifiedWiki?.wikiID,
        sortOrder: modifiedWiki?.sections?.length + 1,
        name: payload?.name,
        linkSet: [],
        created: new Date().toUTCString()
      };
      const wikiSections = [...modifiedWiki.sections, newSectionScaffolding];

      mutateWiki({
        sessionID: discoverySession?.sessionID,
        wikiID: modifiedWiki?.wikiID,
        wikiSections
      })
        .then((res) => {
          onSuccess(res);
          onFailure();
        })
        .catch();
    } else if (action === WIKI_SECTION_CHANGE_EVENTS.REORDER) {
      mutateWiki({
        sessionID: discoverySession?.sessionID,
        wikiID: modifiedWiki?.wikiID,
        wikiSections: payload?.reorderedSections
      })
        .then((res) => {
          onSuccess(res);
          onFailure();
        })
        .catch();
    }
  };

  const shouldShowInputSection = () => {
      return needPlaceholder && props?.editable;
  };

  let placeholder;
  if (shouldShowInputSection()) {
    placeholder = (
      <div
        style={{
          width: "calc(100% - 22.5px)",
          height: "50px",
          margin: "10px 0px 10px 22.5px",
          borderRadius: "4px",
          backgroundColor: "#fff",
          display: "flex",
          alignItems: "center",
          boxShadow:
            "0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%), 0 1px 4px rgb(0 0 0 / 23%)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            marginLeft: "38.5px",
          }}
        >
          <InputBase
            autoFocus={true}
            id="placeholder"
            sx={{
              width: "100%",
              maxWidth: "75%",
              "& .MuiInputBase-input": { textOverflow: "ellipsis" },
              ":hover, :focus-within": { borderBottom: "1px solid #1676d2b3" },
            }}
            value={placeholderValue}
            onChange={handlePlaceholderUpdate}
            placeholder="New Section Title"
            onKeyDown={handleEnterSubmit}
          />
        </div>
        <ConfirmCancelButtons
          addLabel="Add Section"
          confirmFunc={handleAddSection}
          cancelFunc={handlePlaceholderCancel}
        />
      </div>
    );
  }

  if (props === undefined || props.sectionsAtom === undefined) {
    return <LinearProgress />
  }

  const renderWikiSections = React.useMemo(() => {
    const sortedSections = (sections || []).sort((section1, section2) => section1.sortOrder - section2.sortOrder);

    return sortedSections.map((section, idx) => (
      <WikiSection
        setHasSelectedAnyVideosThisSession={setHasSelectedAnyVideosThisSession}
        handleChange={handleWikiChange}
        wikies={props?.wikies}
        section={section}
        key={section?.sectionID}
        allSections={sections}
        idx={idx}
        editable={props?.editable}
      />
    ));
  }, [sections]);

  let videoPanel = undefined;

  if (sessionVideo !== "" && props.videoLibrary === true) {
    videoPanel = <Stack sx={{ minWidth: '50%', minHeight: '500px', padding: '10px' }}
      direction="column"
      justifyContent="flex-start"
      spacing={1}
    >
      <YouTube
        video={sessionVideo}
        autoplay={true}
        modestBranding={true}
        style={{ width: 'auto', minHeight: '500px' }}
      />
    </Stack>
  }
  else if (props?.videoLibrary === true) {
    videoPanel = <Stack sx={{ minWidth: '50%', minHeight: '500px', paddingTop: '10px' }} direction="column" justifyContent={"flex-start"} spacing={1}>
      <Skeleton variant="rounded" animation={false} width={"100%"} height={500} sx={{ backgroundColor: '#cccccc' }} />
    </Stack>
  }

  if (readyForRender === false) {
    return <LinearProgress />
  }

  const renderEmptyWikiSection = () => {
      return (
          <Stack
              sx={{
                  height: "100%",
                  width: "100%",
                  alignItems: "center",
                  justifyContent: "center",
              }}
          >
              <AnnouncementOutlinedIcon
                  sx={{
                      fontSize: "150px",
                      color: theme?.custom?.borderGrey,
                  }}
              />
              No Sections or Videos are Available.
          </Stack>
      );
  };

  const shouldShowWikiDataSection = () => {
    // Show data section if data exist or create button is clicked
    return sections?.length > 0 || needPlaceholder
  }

  return (
    <div
      className="material-wiki-section-root"
      style={{
        width: "100%",
        height: "100%",
        overflow: "auto",
        position: "relative",
        backgroundColor: theme?.custom?.mainBackgroundColor,
        padding: "10px 20px 10px 10px",
      }}
    >
      {shouldShowWikiDataSection() ? (
        <>
          <WikiLoader
            show={isSavingNewSection || isSavingSectionOrders}
            size={60}
          />
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            spacing={0}
            style={{
              width: "100%",
              height: "100%",
            }}
          >
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="sectionsContainer">
                {(provided, snapshot) => {
                  return (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      style={{
                        width: "100%",
                        height: "100%",
                        overflowY: "auto",
                        overflowX: "hidden",
                        paddingRight: "10px",
                      }}
                    >
                      {renderWikiSections}
                      {placeholder}
                      {provided?.placeholder}
                    </div>
                  );
                }}
              </Droppable>
            </DragDropContext>
            {videoPanel}
          </Stack>
        </>
      ) : renderEmptyWikiSection()}
    </div>
  );
}
